import { useRequiredContext } from "@redotech/react-util/context";
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import type { Context } from "react";

/**
 * A dynamic hook to call any method from any RPC client.
 * Accepts a client context as the first argument and retrieves the client from it.
 *
 * @param clientContext - The React context containing the RPC client
 * @param options - Configuration object containing:
 *   - method: The method name to call on the RPC client
 *   - args: Arguments to pass to the RPC client method
 *   - options: Optional react-query options
 *
 * @example
 * ```
 * import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
 *
 * // In your component
 * const { data, isLoading, error } = useRpcQuery(RedoMarketingRpcClientContext, {
 *   method: 'getTeamCampaigns',
 *   args: {
 *     includeStats: true,
 *     searchString: searchQuery,
 *     filters: { status: statusFilters }
 *   },
 *   options: {
 *     enabled: !!searchQuery, // Conditionally enable the query
 *     staleTime: 5 * 60 * 1000, // 5 minutes
 *   }
 * });
 * ```
 */
export function useRpcQuery<
  TClient,
  M extends keyof TClient,
  TArgs extends TClient[M] extends Callable ? Parameters<TClient[M]>[0] : never,
  TData extends TClient[M] extends Callable
    ? Awaited<ReturnType<TClient[M]>>
    : never,
  TError extends Error = Error,
>(
  clientContext: Context<TClient | undefined>,
  {
    method,
    args,
    options,
  }: {
    method: M;
    args: TArgs;
    options?: Omit<
      UseQueryOptions<TData, TError, TData>,
      "queryFn" | "queryKey"
    > &
      Partial<Pick<UseQueryOptions<TData, TError, TData>, "queryKey">>;
  },
) {
  // Get the client instance from context
  const clientInstance = useRequiredContext(clientContext);

  // Use provided queryKey or generate a default one
  // Use the context's displayName for better cache key uniqueness
  const clientName = clientContext.displayName;
  const queryKey = options?.queryKey ?? [clientName, method as string, args];

  return useQuery({
    queryKey,
    queryFn: async () => {
      // Call the RPC method with the provided arguments
      const result = await (clientInstance as any)[method](args);
      return result as TData;
    },
    ...options,
  });
}

type Callable = (...args: any[]) => any;
